/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-22 10:33:30
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component, useState } from 'react';
import { Field } from 'redux-form';
import { InlineError, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderCheckbox2 } from 'src/components/views/common/forms/render-checkbox-2';

import RuleGroupItem from 'src/components/views/recommendation/predictive-bundle/general/rule-group/rule-group-item';
import SelectProductModal from 'src/components/containers/recommendation/predictive-bundle/item/general/select-product-modal';

import {
  PLACEMENT_BUNDLE_OPTIONS,
  DISCOUNT_BUNDLE_TYPES,
  BUNDLE_STATUS_OPTIONS
} from 'src/constants/bundle';
import type { PredictiveBundleWidgetType } from 'src/models/bundle';

type Props = WithI18nProps & {
  formValues: PredictiveBundleWidgetType,
  recommendationWidget: PredictiveBundleWidgetType,
  host: string,
  updateField: Function,
  aimlPermission: boolean,
  syncOrderStatus: {}
};

const LIMIT_PRODUCTS = 4;

// Done
const General = (props: Props): Component<Props, State> => {
  const { i18n, formValues, validateText, updateField, moneyFormat } = props;
  const discountType = _.get(formValues, 'discountType', 'none');

  const [activeSelectProductModal, setActiveSelectProductModal] = useState(false);

  // Handle toggle modal
  const toggleModal = () => {
    setActiveSelectProductModal(!activeSelectProductModal);
  };

  const products = _.get(formValues, 'bundleProducts', '');

  const updateDiscountType = () => {
    // Reset discount value when discount type change
    updateField('discountValue', 0);
  };

  return (
    <div>
      <div className="setup-general-settings">
        <div className="settings">
          <div className="item product">
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.PredictiveBundle.Item.setting01_Label')}
            </TextStyle>
            <p>{i18n.translate('AdminHTML.PredictiveBundle.Item.setting01_SelectField_Label')}</p>
            <div>
              <RuleGroupItem
                products={products}
                updateField={updateField}
                toggleModal={toggleModal}
              />
              {activeSelectProductModal && (
                <SelectProductModal
                  active={activeSelectProductModal}
                  toggleModal={() => setActiveSelectProductModal(false)}
                  limitSelection={LIMIT_PRODUCTS}
                  fieldName="bundleProducts"
                  selectedIndex={-1}
                  rule={products}
                  selectedProducts={products || []}
                  updateField={updateField}
                />
              )}
              <InlineError message={validateText} />
            </div>
          </div>
          <div className="item discount">
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.PredictiveBundle.Item.setting02_Label')}
            </TextStyle>
            <div>
              <Field
                name="discountType"
                label={i18n.translate('AdminHTML.PredictiveBundle.Item.setting02_SelectField01_Label')}
                options={DISCOUNT_BUNDLE_TYPES.map(e => ({
                  value: e.value,
                  label: i18n.translate(e.label)
                }))}
                component={RenderSelect}
                onChange={updateDiscountType}
              />

              {discountType !== 'none' && (
                <Field
                  name="discountValue"
                  label={i18n.translate(
                    'AdminHTML.PredictiveBundle.Item.setting02_SelectField02_Label'
                  )}
                  tooltip={i18n.translate(
                    `AdminHTML.PredictiveBundle.Item.${
                      discountType === 'amount'
                        ? 'setting02_SelectField02_Tooltip02'
                        : 'setting02_SelectField02_Tooltip01'
                    }`
                  )}
                  type="number"
                  min={0}
                  suffix={discountType === 'amount' ? moneyFormat : '%'}
                  component={RenderTextField}
                />
              )}
            </div>
          </div>
          <div className="item placement">
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.PredictiveBundle.Item.setting03_Label')}
            </TextStyle>
            <div>
              <Field
                name="placement"
                label={i18n.translate('AdminHTML.PredictiveBundle.Item.setting03_Content')}
                component={RenderCheckbox2}
                options={PLACEMENT_BUNDLE_OPTIONS.map(e => ({
                  value: e.value,
                  label: i18n.translate(e.label)
                }))}
              />
            </div>
          </div>
          <div className="item status">
            {/* <TextStyle variation="strong">
              {i18n.translate('AdminHTML.PredictiveBundle.Item.setting04_Label')}
            </TextStyle> */}
            <Field
              name="bundleStatus"
              label={
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.PredictiveBundle.Item.setting04_SelectField02_Label')}
                </TextStyle>
              }
              options={BUNDLE_STATUS_OPTIONS.map(e => ({
                value: e.value,
                label: i18n.translate(e.label)
              }))}
              tooltip={i18n.translate(
                'AdminHTML.PredictiveBundle.Item.setting04_SelectField02_Tooltip'
              )}
              component={RenderSelect}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withI18n()(General);
